import { useEffect } from 'react';
import { collection, query, where, getDocs, writeBatch, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

// =====================================================================
// 🔐 AUDITORÍA DE SEGURIDAD — qué había mal y qué cambia
//
// ANTES: este hook corre en CADA carga de página de CUALQUIER barbería. Si la
// URL traía ?payment_status=success&ticketId=XXXX, marcaba TODAS las filas de
// esa reserva como status "confirmed" + paymentMethod "bancard" + paidAt, sin
// verificar nada. Cualquiera podía escribir esa URL a mano con el número de
// ticket de otra persona (son consecutivos: CAPEL_01234) para:
//   - "confirmar" una reserva sin haber pagado (en Capelli: saltearse la seña),
//   - revivir una reserva cancelada o volver a "confirmada" una ya completada
//     (con lo que se pierden comisiones y se desordena la caja),
//   - cambiar a "bancard" el método de pago de cualquier turno,
//   - y disparar desde su navegador el WhatsApp y el correo de "reserva
//     confirmada" al cliente real (ver el callback en App.js).
// Y todo esto funcionaba aunque ninguna barbería tenga pago online.
//
// AHORA, el retorno solo se acepta si:
//   1) ESTE navegador fue el que inició el pago de ESE ticket (App.js guarda
//      localStorage.paymentTicket al abrir el modal de Bancard). Un enlace
//      escrito a mano desde otro navegador ya no hace nada.
//   2) Nunca se toca una fila cancelada.
//   3) El estado solo pasa a "confirmed" si estaba "pending" (el caso del
//      cliente que reservó con pago online). Una reserva "completed" o
//      "in_progress" conserva su estado; solo se registra cómo se pagó.
//
// LIMITACIÓN (honesta): esto frena el abuso por enlace, pero la marca vive en el
// navegador, así que no es una prueba de pago. La solución definitiva es que
// Bancard avise al SERVIDOR (confirmation URL firmada) y el servidor marque la
// reserva — queda para antes de activar el primer pago online (ver informe, S15).
// =====================================================================

const usePaymentCheck = (setLoading, addToast, reloadBookings, setBookingState) => {
  useEffect(() => {
    const checkReturn = async () => {
      const params = new URLSearchParams(window.location.search);
      const status = params.get('payment_status');
      const ticketId = params.get('ticketId');

      const limpiarUrl = () => window.history.replaceState({}, document.title, window.location.pathname);

      if (status === 'success' && ticketId) {
        // 1) ¿Este navegador inició el pago de este ticket?
        let ticketIniciado = null;
        try { ticketIniciado = localStorage.getItem('paymentTicket'); } catch (e) { /* navegador restringido */ }
        if (!ticketIniciado || String(ticketIniciado) !== String(ticketId)) {
          console.warn('[Pago] Retorno ignorado: este navegador no inició el pago del ticket', ticketId);
          limpiarUrl();
          return;
        }

        setLoading(true);
        try {
          const q = query(collection(db, 'bookings'), where('bookingGroupId', '==', ticketId));
          const snap = await getDocs(q);

          if (!snap.empty) {
            const data = snap.docs[0].data();
            const batch = writeBatch(db);
            let hayAlgoParaActualizar = false;

            snap.forEach((d) => {
              const estadoActual = d.data().status;
              if (estadoActual === 'cancelled') return; // 2) nunca se revive una cancelada

              const cambios = { paymentMethod: 'bancard', paidAt: Timestamp.now() };
              if (estadoActual === 'pending') cambios.status = 'confirmed'; // 3) solo pending → confirmed
              batch.update(d.ref, cambios);
              hayAlgoParaActualizar = true;
            });

            if (hayAlgoParaActualizar) {
              await batch.commit();

              // Actualizar estado visual
              setBookingState(data);
              await reloadBookings();
            }

            // La marca se usa una sola vez
            try { localStorage.removeItem('paymentTicket'); } catch (e) { /* navegador restringido */ }
            limpiarUrl();

            // (Sin toast de "Pago exitoso": el cliente ve la pantalla de éxito de la App.)
          } else {
            try { localStorage.removeItem('paymentTicket'); } catch (e) { /* navegador restringido */ }
            limpiarUrl();
          }
        } catch (error) {
          console.error(error);
          addToast('Error', 'Error validando pago.', 'error');
        } finally {
          setLoading(false);
        }
      } else if (status === 'cancelled') {
        addToast('Cancelado', 'El pago no se completó.', 'info');
        limpiarUrl();
      }
    };

    checkReturn();
  }, [setLoading, addToast, reloadBookings, setBookingState]);
};

export default usePaymentCheck;